/**
 * Login route — email OTP flow.
 *
 * Enter email → Supabase sends magic-link/OTP via Resend
 * (theline@orchist.org) → invitee clicks link → lands on /auth/callback
 * → session established → redirect to home.
 */

import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function Login() {
  const { loading, session, signInWithOtp } = useAuth();
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (loading) {
    return <MinimalShell>...</MinimalShell>;
  }

  // Already authenticated → redirect home
  if (session) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim() || submitState === 'sending') return;

    setSubmitState('sending');
    setErrorMessage(null);

    const { error } = await signInWithOtp(email.trim());

    if (error) {
      setSubmitState('error');
      setErrorMessage(error.message ?? 'Unable to send the invitation email. Please try again.');
      return;
    }

    setSubmitState('sent');
  }

  return (
    <MinimalShell>
      {submitState === 'sent' ? (
        <div className="space-y-6 text-center">
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">Check your inbox.</span>
          </h1>
          <p className="text-lg text-ink/75 leading-relaxed max-w-md mx-auto">
            A message has been sent to{' '}
            <strong className="text-ink">{email}</strong>. Click the link inside
            to enter. It expires in one hour.
          </p>
          <p className="text-sm text-ink/50 pt-4">
            Nothing in your inbox? Check spam. Still nothing?{' '}
            <button
              type="button"
              onClick={() => {
                setSubmitState('idle');
                setErrorMessage(null);
              }}
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              Try again
            </button>
            .
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="text-center space-y-4">
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">The Line</p>
            <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
              Enter.
            </h1>
            <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
              The Line recognizes you by the address you are known at.
            </p>
          </div>

          <div className="space-y-3">
            <label htmlFor="email" className="sr-only">
              Email address
            </label>
            <input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              placeholder="you@somewhere.world"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitState === 'sending'}
              className="w-full px-5 py-4 border border-ink/20 bg-paper/50 text-lg font-display placeholder:text-ink/30 focus:outline-none focus:border-ink/60 transition-colors disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={submitState === 'sending' || !email.trim()}
              className="w-full px-5 py-4 bg-ink text-paper font-display text-lg tracking-wide hover:bg-ink/85 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitState === 'sending' ? 'Sending...' : 'Send me the link'}
            </button>
          </div>

          {errorMessage && (
            <p className="text-sm text-destructive text-center" role="alert">
              {errorMessage}
            </p>
          )}

          <p className="text-xs text-ink/50 text-center pt-6">
            <Link
              to="/"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60"
            >
              &larr; Back
            </Link>
          </p>
        </form>
      )}
    </MinimalShell>
  );
}

function MinimalShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-lg relative">{children}</div>
    </main>
  );
}
